import React, { useState, useEffect } from 'react';
import { Target, Eye, Award, Users, Code, Zap, Heart, Rocket } from 'lucide-react';
import { supabase } from '../supabaseClient';

const About = () => {
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const { data, error } = await supabase
        .from('about_content')
        .select('*')
        .single();
      
      if (error) throw error;
      setAboutContent(data);
    } catch (error) {
      console.error('Error fetching about content:', error);
      // Set default content
      setAboutContent({
        title: 'About Script Saint',
        subtitle: 'Empowering developers with production-ready code',
        mission: 'Our mission is to accelerate development by providing high-quality, well-documented, and production-ready code solutions that developers can trust and deploy with confidence.',
        vision: 'To become the go-to marketplace for developers seeking reliable, enterprise-grade code solutions that save time and reduce technical debt.',
        story: 'Founded by experienced developers who understood the pain of building everything from scratch, Script Saint was created to bridge the gap between rapid development and quality code. We believe that developers should focus on solving unique problems, not reinventing the wheel.',
        values: [
          {
            title: 'Quality First',
            description: 'Every line of code is crafted with production standards in mind',
            icon: 'award'
          },
          {
            title: 'Developer-Focused',
            description: 'Built by developers, for developers. We understand your needs',
            icon: 'users'
          },
          {
            title: 'Innovation',
            description: 'Staying ahead with the latest technologies and best practices',
            icon: 'zap'
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { number: '500+', label: 'Projects Delivered', icon: Rocket },
    { number: '1000+', label: 'Happy Developers', icon: Users },
    { number: '50+', label: 'Products', icon: Code },
    { number: '99%', label: 'Satisfaction Rate', icon: Heart }
  ];

  const getIcon = (iconName) => {
    const icons = {
      award: Award,
      users: Users,
      zap: Zap,
      code: Code
    };
    const Icon = icons[iconName] || Award;
    return <Icon size={36} className="text-gray-900" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container space-y-32 relative z-10">
        {/* Hero Section */}
        <section className="text-center">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-8">
            <Award size={16} className="text-cyan-400" />
            <span>Our Story</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            {aboutContent?.title || 'About'} <span className="gradient-text">Script Saint</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            {aboutContent?.subtitle || 'Empowering developers with production-ready code'}
          </p>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="glass-card p-8 text-center card-hover group relative overflow-hidden rounded-2xl border border-cyan-500/10">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                    <Icon size={24} className="text-gray-900" />
                  </div>
                  <div className="text-5xl font-bold gradient-text mb-2">{stat.number}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Mission & Vision */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="glass-card p-12 card-hover group relative overflow-hidden rounded-3xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl shadow-cyan-500/30">
                <Target className="text-gray-900" size={36} />
              </div>
              <h2 className="text-4xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">Our Mission</h2>
              <p className="text-gray-400 leading-relaxed text-lg">
                {aboutContent?.mission}
              </p>
            </div>
          </div>

          <div className="glass-card p-12 card-hover group relative overflow-hidden rounded-3xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl shadow-cyan-500/30">
                <Eye className="text-gray-900" size={36} />
              </div>
              <h2 className="text-4xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">Our Vision</h2>
              <p className="text-gray-400 leading-relaxed text-lg">
                {aboutContent?.vision}
              </p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="glass-card p-16 rounded-3xl relative overflow-hidden border border-cyan-500/10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="text-5xl font-bold font-poppins mb-8 text-center">
              Our <span className="gradient-text">Story</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed max-w-4xl mx-auto text-center">
              {aboutContent?.story}
            </p>
          </div>
        </section>

        {/* Values */}
        <section>
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold font-poppins mb-6">
              Our <span className="gradient-text">Values</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {aboutContent?.values?.map((value, index) => (
              <div key={index} className="glass-card p-10 text-center card-hover group relative overflow-hidden rounded-2xl border border-cyan-500/10">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl shadow-cyan-500/30">
                    {getIcon(value.icon)}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="glass-card p-16 text-center rounded-3xl border-2 border-cyan-500/20 relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold font-poppins mb-4">
              Ready to Work <span className="gradient-text">With Us</span>?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of developers building better products faster
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/shop" className="gradient-button inline-flex items-center justify-center space-x-2 text-lg px-10 py-5 rounded-xl shadow-lg shadow-cyan-500/30">
                <span>Browse Products</span>
              </a>
              <a href="/contact" className="glass-card inline-flex items-center justify-center px-10 py-5 rounded-xl text-gray-300 hover:text-white hover:border-cyan-500/30 transition-all">
                <span>Get in Touch</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;